// Single source of truth for the Settings workspace's two-level taxonomy:
// four sections (L1) each with one or more pages (L2).
//
// Consumed by:
//   - SettingsLayout.jsx / settingsLayoutLogic.mjs (L1 section bar + L2
//     page sub-nav — see that module for the permission-filtered derivation)
//   - SettingsIndexRedirect.jsx / settingsIndexLogic.mjs (where bare
//     /app/settings sends you)
//   - sidebarNav.mjs (union requireAny for the single 'Settings' leaf)
//
// A section's `requireAny` is COMPUTED as the de-duped union of its pages'
// requireAny (never hand-maintained) so it can't drift from what the L2
// tabs actually gate: a section is visible iff the user can reach at
// least one of its pages, full stop.
//
// Settings IA rebuild (2026-07): System Overview (AdminPanel) and Alert
// Detector (AlertsConfigPage) used to live here as a 'system' section.
// Both are operational tools, not configuration, and have been relocated
// to top-level routes (/app/system, /app/alerts/detector — see App.jsx /
// sidebarNav.mjs). There is no 'system' section here anymore; don't
// re-add one without relocating those pages back on purpose.

const RAW_SECTIONS = {
  content: {
    key: 'content',
    label: 'Content',
    icon: 'FileCode',
    pages: [
      {
        key: 'landing-content',
        label: 'Landing Content',
        path: 'content/landing-content',
        icon: 'FileCode',
        requireAny: ['manage_landing'],
      },
      {
        // ImageManager also manages the partner-logo roster (server-side
        // gated on manage_partners — see services/auth/tests/public-write-auth.test.js)
        // in addition to team photos (manage_images), so this tab must stay
        // reachable for a manage_partners-only user too.
        key: 'images',
        label: 'Marketing Images',
        path: 'content/images',
        icon: 'ImageIcon',
        requireAny: ['manage_images', 'manage_partners'],
      },
      {
        // App logo + favicon upload. 'manage_branding' is an intentionally
        // UNSEEDED sentinel permission: no role is granted it, so the
        // super_admin bypass in hasAnyPermission() is the only way this tab
        // shows — keeping it super-admin-only WITHOUT a migration, and
        // matching the server gate (routes/public.js POST /branding uses
        // requireSuperAdmin, not a permission check). Do not seed
        // manage_branding to another role without meaning to widen access.
        key: 'branding',
        label: 'Branding',
        path: 'content/branding',
        icon: 'Palette',
        requireAny: ['manage_branding'],
      },
    ],
  },
  people: {
    key: 'people',
    label: 'People',
    icon: 'UsersIcon',
    pages: [
      {
        key: 'users',
        label: 'Users',
        path: 'people/users',
        icon: 'UsersIcon',
        requireAny: ['manage_users'],
      },
      {
        key: 'roles',
        label: 'Roles & Permissions',
        path: 'people/roles',
        icon: 'Shield',
        requireAny: ['manage_users'],
      },
      {
        key: 'access-requests',
        label: 'Access Requests',
        path: 'people/access-requests',
        icon: 'Mail',
        requireAny: ['approve_signups'],
      },
    ],
  },
  runtime: {
    key: 'runtime',
    label: 'Maps & Locale',
    icon: 'MapPin',
    pages: [
      {
        // Single page — has its own INTERNAL Boundary/Presentation/
        // Localization tabs (RuntimeSettingsPage.jsx + runtimeSettingsSectionNavLogic.mjs).
        // Leave those untouched; they are a separate, nested tab strip
        // inside this one page, not additional L2 entries here.
        key: 'runtime',
        label: 'Runtime configuration',
        path: 'runtime',
        icon: 'MapPin',
        requireAny: ['manage_settings'],
      },
    ],
  },
  models: {
    key: 'models',
    label: 'Models',
    icon: 'Cpu',
    pages: [
      {
        // Admin GUI over the run-worker pool (migration 0034 +
        // runController.js run-settings API): concurrency cap, resource-aware
        // hold thresholds, and failed-run cleanup. GET/PUT are manage_settings;
        // cleanup is manage_models — page is reachable with either, controls
        // gate individually inside ModelRunsSettingsPage.
        key: 'runs',
        label: 'Model Runs',
        path: 'models/runs',
        icon: 'Cpu',
        requireAny: ['manage_settings', 'manage_models'],
      },
    ],
  },
  alerts: {
    key: 'alerts',
    label: 'Alerts',
    icon: 'Bell',
    pages: [
      {
        // PR #43, Track B — admin GUI for Track A's /api/alerts/notifications
        // contract. This is the anomaly-alert NOTIFICATION config (who gets
        // pinged, on what channel/schedule); not to be confused with the
        // Alert Detector (WHO seasonal-month profile tuning), which is an
        // operational tool relocated to /app/alerts/detector, outside Settings.
        key: 'notifications',
        label: 'Alert Notifications',
        path: 'alerts/notifications',
        icon: 'Bell',
        requireAny: ['manage_alert_notifications'],
      },
    ],
  },
  integrations: {
    key: 'integrations',
    label: 'Integrations',
    icon: 'Plug',
    pages: [
      {
        // GUI over the already-shipped, security-reviewed
        // /api/integrations registry (DHIS2, SMTP, SMS gateways, generic
        // REST APIs). Super-admin gated both here and server-side.
        key: 'registry',
        label: 'Integrations',
        path: 'integrations/registry',
        icon: 'Plug',
        requireAny: ['manage_integrations'],
      },
      {
        // The data-driven type/field-schema catalog (migration 0027) that
        // the registry's own type picker + credential fields are fetched
        // from — /api/integration-types, same manage_integrations gate.
        key: 'types',
        label: 'Integration Types',
        path: 'integrations/types',
        icon: 'Layers',
        requireAny: ['manage_integrations'],
      },
    ],
  },
}

// requireAny per section = de-duped union of its pages' requireAny.
export const SETTINGS_SECTIONS = Object.fromEntries(
  Object.entries(RAW_SECTIONS).map(([key, section]) => [
    key,
    { ...section, requireAny: [...new Set(section.pages.flatMap((p) => p.requireAny))] },
  ])
)

// Priority order used both for the L1 tab bar and for where bare
// /app/settings lands (SettingsIndexRedirect walks this in order).
export const SETTINGS_SECTION_ORDER = ['content', 'people', 'runtime', 'models', 'alerts', 'integrations']

// Which section (if any) a /app/settings/* pathname belongs to. Section
// route bases (content / people / runtime / alerts) don't overlap or
// prefix each other, so a plain prefix match is unambiguous. Returns null
// for bare /app/settings (no section yet — SettingsIndexRedirect renders
// there) or any path outside the workspace.
export function sectionForPath(pathname) {
  if (typeof pathname !== 'string') return null
  for (const key of SETTINGS_SECTION_ORDER) {
    if (pathname.startsWith(`/app/settings/${key}`)) return SETTINGS_SECTIONS[key]
  }
  return null
}

// Pages within a section, in taxonomy order; [] for an unknown key.
export function pagesForSection(key) {
  return SETTINGS_SECTIONS[key]?.pages || []
}
